import React, { useContext, useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'umi';
import { getApiData } from '@/pages/dashboard/services/common';
import moment from 'moment';
import { FilterContext } from '../../../PageContainer/context';
import { IS_INTLGAME, IS_WOA } from '@home/_config/config';

const ApplicableDomain = {
  WOA: "woa",
  INTLGAME: "intlgame",
  ALL: "all",
} as const;

type TApplicableDomain = typeof ApplicableDomain[keyof typeof ApplicableDomain];

export default function VsBenchmarkService(props: any) {
  const {
    className,
    style,
    api,
    children,
    metrics,
    extraParams,
    benchmarkMetrics,
    benchmarkGameList,
    topMianKeyMap: topMianKeyMapProps,
    timeZone,
    dayOffset = -1,
    gameListUrl,
    applicableDomain = ApplicableDomain.ALL,
    filter: filterProps,
    // 是否允许在游戏上线之前展示 Vs Benchmark
    showVsBeforeRelease = false,
    ...restProps
  }: {
    [key: string]: any;
    applicableDomain?: TApplicableDomain;
  } = props;

  const topMianKeyMap = topMianKeyMapProps || {};

  const { currentGame, releaseTime } = useSelector(
    (state: any) => ({
      currentGame: state?.dashboard?.currentGame,
      releaseTime:
        moment(state?.dashboardSetting?.gameConfig?.game_phase_timeline?.release_time)?.format('YYYY-MM-DD'),
    }),
    shallowEqual,
  );
  const {
    game_code: gameCode,
    game_name: gameName,
    cover: currentCover,
  } = currentGame || {};

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [topMedianData, setTopMedianData] = useState<any>({});
  const [gameListData, setGameListData] = useState<any>({});

  const contextObj = useContext(FilterContext);
  const { filterVal } = contextObj || {};
  
  const { genre_list = ['SOC'] } = filterProps || {};

  // 配置>筛选器
  const timeZoneNew = timeZone || filterVal?.timeZone || 0;

  // 判断当前域名是否适用 Vs Benchmark
  const isDomainApplicable = (() => {
    if (applicableDomain === ApplicableDomain.ALL) return true;
    if (applicableDomain === ApplicableDomain.WOA) return IS_WOA;
    if (applicableDomain === ApplicableDomain.INTLGAME) return IS_INTLGAME;
    return false;
  })();

  // 默认T-1天可配置
  const defaultEndTime = moment().utcOffset(timeZoneNew).add(dayOffset, 'day').format('YYYY-MM-DD');
  const hasVs = (moment(defaultEndTime).isSameOrAfter(moment(releaseTime)) || showVsBeforeRelease) && isDomainApplicable;

  useEffect(() => {
    // 没有api/上线时间/上线时间小于当前默认时间不请求接口
    if (!api || !releaseTime || !hasVs) return;
    setLoading(true);
    const benchmarkMetricList: string[] = [];

    // 遍历出所有配置Benchmark竞品的指标
    Object.keys(benchmarkMetrics).forEach((key) => {
      const currentMetricList: string[] =
        Object.keys(benchmarkMetrics[key])?.map(
          (valueKey) => benchmarkMetrics[key]?.[valueKey],
        ) || [];
      benchmarkMetricList.push(...currentMetricList);
    });

    const currentGameData = getApiData(api, {
      game_code: gameCode,
      period: 'daily',
      filters: {
        zone_list: ['255'],
        market_list: ['255'],
        channel_list: ['255'],
        os_list: ['255'],
        lang_list: ['255'],
      },
      start_time: releaseTime,
      end_time: defaultEndTime,
      metrics,
      ...(extraParams || {}),
    });
    const benchmarkMetricData = getApiData(
      '/api/v2/dashboard/benchmark/metric',
      {
        filters: {
          genre_list,
          game_list: [],
        },
        frame_type: 'PC&Console',
        has_game_contributors: true,
        metrics: benchmarkMetricList,
      },
    );
    const benchmarkGameData = getApiData(
      '/api/v2/dashboard/benchmark/overall_performance',
      {
        filters: {
          genre_list,
          game_list: benchmarkGameList,
          platform_list: ['PC&Console'],
        },
        accumulation: {
          by_game: true,
          by_genre: false,
          by_region: false,
          by_country: false,
          by_platform: false,
        },
        desc: true,
        value_type: 'value',
        frame_type: 'PC&Console',
        is_page: false,
        metrics: benchmarkMetricList,
      },
    );

    Promise.allSettled([currentGameData, benchmarkMetricData, benchmarkGameData])
      .then((data) => {
        const [currentDataResult, topMianDataResult, benchmarkDataResult] = data;

        const currentData = currentDataResult.status === 'fulfilled' ? currentDataResult.value : {};
        const topMianData = topMianDataResult.status === 'fulfilled' ? topMianDataResult.value : {};
        const benchmarkData = benchmarkDataResult.status === 'fulfilled' ? benchmarkDataResult.value : {};

        const topMianDataMap: any = {};
        const gameDataMap: any = {};
        Object.keys(benchmarkMetrics || {}).forEach((key) => {
          const currentMetric = benchmarkMetrics[key] || {};

          // 组装topMian数据
          const topMianItemList: any[] = [];
          Object.keys(topMianKeyMap || {}).forEach((tmKey) => {
            const topMianItem: any = { key: tmKey };
            // 组装成d1,w1,m1
            Object.keys(currentMetric).forEach((k) => {
              topMianItem[k] =
                topMianData?.[currentMetric[k]]?.[topMianKeyMap[tmKey]];
            });
            topMianItemList.push(topMianItem);
          });
          topMianDataMap[key] = topMianItemList;

          // 组装游戏数据
          const gameDataList: any[] = [];
          // 组装当前游戏数据
          const itemObject: any = {
            gameName,
            cover: currentCover,
            isCurrentGame: true,
            gamesDetailsUrl: gameListUrl[gameCode]
          };
          // 根据指标key获取对应数据
          Object.keys(currentData || {}).forEach((k: string) => {
            itemObject[k] = currentData[k]?.find(
              (item: any) => item?.metrics_name === key,
            )?.value;
          });
          gameDataList.push(itemObject);

          // 组装竞品数据
          const games = benchmarkData?.metrics_list || [];
          games.forEach((item: any) => {
            const { game_name, cover, metrics, combined_id } = item || {};
            const gameItem: any = { gameName: game_name, cover, gamesDetailsUrl: gameListUrl[combined_id] };
            // 组装成d1,w1,m1
            Object.keys(currentMetric).forEach((k) => {
              gameItem[k] = metrics?.[benchmarkMetrics[key][k]]?.value;
            });
            gameDataList.push(gameItem);
          });

          gameDataMap[key] = gameDataList;
        });
        setTopMedianData(topMianDataMap);
        setGameListData(gameDataMap);
      })
      .catch((err) => {
        console.log('useEffect ~ vsBenchmarkApi>>', err);
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [api, benchmarkGameList, benchmarkMetrics, currentCover, defaultEndTime, extraParams, gameCode, gameName, hasVs, metrics, releaseTime, topMianKeyMap]);

  return (
    <div className={className} style={style}>
      {React.Children.map(children, (child: any) =>
        React.cloneElement(child, {
          topMedianData,
          gameListData,
          currentGameReleaseTime: releaseTime,
          vsBenchmarkLoading: loading,
          vsBenchmarkError: error,
          defaultEndTime,
          hasVs,
          ...restProps,
        }),
      )}
    </div>
  );
}
